import { IRouterInfo } from '@/store/slices/routeSlice';
import logger from '@/utils/client-logger';
import {
  getRouteCnName,
  getRouteComponent,
  getRouteMetadata,
  getRouteSeoMeta,
} from '@/utils/route-metadata';
import { useRef } from 'react';

const useRouteInfo = () => {
  // sessionStorage 键值定义
  const STORAGE_KEYS = {
    CURRENT_ROUTE: 'zl-current-route-info',
    FROM_ROUTE: 'zl-from-route-info',
  };

  // 从 sessionStorage 获取路由信息的工具函数
  const getRouteInfoFromStorage = (key: string): IRouterInfo => {
    if (typeof window === 'undefined') {
      return { refer_page_name: '其他', refer_url_path: '', refer_path_name: '' };
    }

    try {
      const item = sessionStorage.getItem(key);
      if (item) {
        return JSON.parse(item) as IRouterInfo;
      }
    } catch (e) {
      logger.error(`getRouteInfoFromStorage ${key}`, e);
    }

    // 如果是获取来源路由信息且没有数据，返回空值
    if (key === STORAGE_KEYS.FROM_ROUTE) {
      return { refer_page_name: '', refer_url_path: '', refer_path_name: '' };
    }

    return { refer_page_name: '其他', refer_url_path: '', refer_path_name: '' };
  };

  const getFromRouterInfo = (): IRouterInfo => {
    if (typeof window === 'undefined') {
      return { refer_page_name: '其他', refer_url_path: '', refer_path_name: '' };
    }

    // 直接返回存储的路由信息，如果没有就返回空值
    // PathnameTracker 已经在页面刷新时清除了 sessionStorage
    return getRouteInfoFromStorage(STORAGE_KEYS.FROM_ROUTE);
  };

  // =============================================
  // 基础路由信息 Hooks
  // =============================================

  // 获取来源路由信息
  const useFromRouterInfo = (): {
    refer_page_name?: string;
    refer_url_path?: string;
    refer_path_name?: string;
    group_id?: string | number;
    group_name?: string;
    product_sku?: string;
    shop_sku?: string;
  } => {
    return getRouteInfoFromStorage(STORAGE_KEYS.FROM_ROUTE);
  };

  // 获取来源路由信息（稳定版本 - 只返回首次渲染的值）
  const useFromRouterInfoStable = (): {
    refer_page_name?: string;
    refer_url_path?: string;
    refer_path_name?: string;
    group_id?: string | number;
    group_name?: string;
    product_sku?: string;
    shop_sku?: string;
  } => {
    const stableRef = useRef<{
      refer_page_name?: string;
      refer_url_path?: string;
      refer_path_name?: string;
      group_id?: string | number;
      group_name?: string;
      product_sku?: string;
      shop_sku?: string;
    } | null>(null);

    // 只在首次渲染时获取路由信息，后续渲染都返回相同值
    if (stableRef.current === null) {
      stableRef.current = getRouteInfoFromStorage(STORAGE_KEYS.FROM_ROUTE);
    }

    return stableRef.current;
  };

  // 获取当前路由信息
  const useCurrentRouteInfo = (): {
    refer_page_name?: string;
    refer_url_path?: string;
    refer_path_name?: string;
    group_id?: string | number;
    group_name?: string;
    product_sku?: string;
    shop_sku?: string;
  } => {
    return getRouteInfoFromStorage(STORAGE_KEYS.CURRENT_ROUTE);
  };

  // =============================================
  // 上一个页面信息 Hooks
  // =============================================

  // 获取上一个页面的路径名（保持向后兼容）
  const usePreviousPathname = () => {
    const fromRouterInfo = getRouteInfoFromStorage(STORAGE_KEYS.FROM_ROUTE);
    return fromRouterInfo.refer_url_path;
  };

  // 获取上一个页面的页面名称（英文）
  const usePreviousPageName = () => {
    const fromRouterInfo = getRouteInfoFromStorage(STORAGE_KEYS.FROM_ROUTE);
    return fromRouterInfo.refer_page_name;
  };

  // 获取上一个页面的中文名称
  const usePreviousPageCnName = () => {
    const fromRouterInfo = getRouteInfoFromStorage(STORAGE_KEYS.FROM_ROUTE);
    return getRouteCnName(fromRouterInfo.refer_url_path);
  };

  // 获取上一个页面的完整元数据
  const usePreviousRouteMetadata = () => {
    const fromRouterInfo = getRouteInfoFromStorage(STORAGE_KEYS.FROM_ROUTE);
    return getRouteMetadata(fromRouterInfo.refer_url_path);
  };

  // =============================================
  // 当前页面信息 Hooks
  // =============================================

  // 获取当前页面的路径名
  const useCurrentPathname = () => {
    const currentRouteInfo = getRouteInfoFromStorage(STORAGE_KEYS.CURRENT_ROUTE);
    return currentRouteInfo.refer_url_path;
  };

  // 获取当前页面的页面名称（英文）
  const useCurrentPageName = () => {
    const currentRouteInfo = getRouteInfoFromStorage(STORAGE_KEYS.CURRENT_ROUTE);
    return currentRouteInfo.refer_page_name;
  };

  // 获取当前页面的中文名称
  const useCurrentPageCnName = () => {
    const currentRouteInfo = getRouteInfoFromStorage(STORAGE_KEYS.CURRENT_ROUTE);
    return getRouteCnName(currentRouteInfo.refer_url_path);
  };

  // 获取当前页面的完整元数据
  const useCurrentRouteMetadata = () => {
    const currentRouteInfo = getRouteInfoFromStorage(STORAGE_KEYS.CURRENT_ROUTE);
    return getRouteMetadata(currentRouteInfo.refer_url_path);
  };

  // 获取当前页面的SEO元数据
  const useCurrentRouteSeoMeta = () => {
    const currentRouteInfo = getRouteInfoFromStorage(STORAGE_KEYS.CURRENT_ROUTE);
    return getRouteSeoMeta(currentRouteInfo.refer_url_path);
  };

  // 获取当前页面的组件名称
  const useCurrentRouteComponent = () => {
    const currentRouteInfo = getRouteInfoFromStorage(STORAGE_KEYS.CURRENT_ROUTE);
    return getRouteComponent(currentRouteInfo.refer_url_path);
  };

  return {
    getFromRouterInfo,
    useFromRouterInfo,
    useFromRouterInfoStable,
    useCurrentRouteInfo,
    usePreviousPathname,
    usePreviousPageName,
    usePreviousPageCnName,
    usePreviousRouteMetadata,
    useCurrentPathname,
    useCurrentPageName,
    useCurrentPageCnName,
    useCurrentRouteMetadata,
    useCurrentRouteSeoMeta,
    useCurrentRouteComponent,
  };
};

export default useRouteInfo;
